import type { TankReadingConfig } from '@/api/hooks/useTenantConfig'
import { Switch, Tooltip, theme } from 'antd'
import './ShareReadingsCell.css'
import {
  type ShareReadingState,
  deriveShareReadingState,
} from './shareReadings'

interface ShareReadingsCellProps {
  config: TankReadingConfig
  onToggle: (config: TankReadingConfig, next: boolean) => void
  // Sending requires an S&D instance that pushes readings into Crossroads; without one
  // there is nothing to forward, so the send toggle is locked.
  sendDisabled?: boolean
}

export const ShareReadingsCell = ({
  config,
  onToggle,
  sendDisabled = false,
}: ShareReadingsCellProps) => {
  const { token } = theme.useToken()
  const { state, on, label, mode } = deriveShareReadingState(config)

  // Only the genuinely-on states fill the track. `available` (a partner is
  // offering but receive isn't enabled) renders as a plain off switch so it
  // can't be mistaken for active.
  const stateColor: Record<ShareReadingState, string | undefined> = {
    available: undefined,
    receiving: token.colorSuccess,
    'sending-active': token.colorSuccess,
    'sending-pending': token.colorPrimary,
    off: undefined,
  }
  const color = stateColor[state]

  // Lock only the off->on direction so an already-on send can always be turned back off.
  const disabled = mode === 'send' && !on && sendDisabled

  const sw = (
    <Switch
      checked={on}
      disabled={disabled}
      checkedChildren={label}
      unCheckedChildren={label}
      className='share-switch'
      style={color ? { backgroundColor: color } : undefined}
      onClick={() => onToggle(config, !on)}
    />
  )

  if (!disabled) return sw

  return (
    <Tooltip title='Enable "Send Tank Readings" on one of your instances to share readings.'>
      {/* span wrapper so the tooltip still fires over the disabled switch */}
      <span style={{ display: 'inline-flex' }}>{sw}</span>
    </Tooltip>
  )
}
